import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Search, RefreshCw, Loader2, Globe, Monitor } from "lucide-react";
import api, { apiError } from "../../lib/api";

const OUTCOMES = {
  sent: { label: "Sent", bg: "#DCFCE7", fg: "#166534" },
  throttled: { label: "Throttled", bg: "#E5E7EB", fg: "#374151" },
  rate_limited: { label: "Rate limited", bg: "#FEE2E2", fg: "#991B1B" },
  no_account: { label: "No account", bg: "#FFEDD5", fg: "#9A3412" },
};

// Short "Browser · OS" summary; full user agent stays in the tooltip.
export function deviceSummary(ua = "") {
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox"
    : /CriOS|Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /okhttp|Dart|Expo|CFNetwork/i.test(ua) ? "Mobile app" : "Other";
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android" : /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "";
  return ua ? [browser, os].filter(Boolean).join(" · ") : "—";
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" }) : "—");

function OutcomeBadge({ outcome, testid }) {
  const o = OUTCOMES[outcome] || { label: outcome || "—", bg: "#E5E7EB", fg: "#374151" };
  return <span data-testid={testid} className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: o.bg, color: o.fg }}>{o.label}</span>;
}

export default function SecurityLog() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [applied, setApplied] = useState("");
  const [loading, setLoading] = useState(true);
  const retry = useRef(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/security/reset-requests", { params: { limit: 500, ...(q ? { email: q } : {}) } });
      setRows(data);
      clearTimeout(retry.current);
      if (data.some((r) => r.country == null)) retry.current = setTimeout(() => load(q), 5000);
    } catch (e) {
      if (e?.response?.status === 403) { nav("/reports", { replace: true }); return; }
      toast.error(apiError(e));
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { load(""); return () => clearTimeout(retry.current); }, [load]);

  const search = (e) => { e.preventDefault(); const q = email.trim().toLowerCase(); setApplied(q); load(q); };

  return (
    <div data-testid="security-log-page">
      <div className="flex items-center gap-2"><ShieldAlert className="text-amber-500" /><h1 className="text-2xl font-extrabold text-white">Security log</h1></div>
      <p className="text-slate-400 text-sm">Password reset requests, newest first. Kept for 90 days.</p>

      <form onSubmit={search} className="mt-5 flex flex-wrap gap-2" data-testid="security-filter-form">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Filter by email" data-testid="security-email-filter"
          className="flex-1 min-w-[220px] bg-[#0F172A] border border-slate-700 text-slate-100 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none" />
        <button type="submit" data-testid="security-search-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors"><Search size={15} /> Search</button>
        <button type="button" onClick={() => load(applied)} disabled={loading} data-testid="security-refresh-btn" className="border border-slate-700 text-slate-200 hover:border-slate-500 text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-60">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </form>
      {applied && <p className="text-xs text-slate-500 mt-2" data-testid="security-filter-active">Showing results for {applied} · <button onClick={() => { setEmail(""); setApplied(""); load(""); }} className="text-amber-500 font-semibold" data-testid="security-clear-filter">Clear</button></p>}

      <div className="mt-4 bg-[#0F172A] border border-slate-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="security-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              {["Date & time", "Email", "Role", "IP", "Location", "Device / browser", "Outcome"].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center"><Loader2 className="animate-spin text-slate-500 inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-500" data-testid="security-empty">No reset requests found.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`security-row-${r.id}`} className="border-b border-slate-800/70 last:border-0 hover:bg-slate-800/30">
                <td className="px-4 py-3 text-slate-200 whitespace-nowrap">{fmtDate(r.requested_at)}</td>
                <td className="px-4 py-3 text-slate-200 break-all">{r.email}{r.account_exists === false && <span className="block text-[11px] text-slate-500">not registered</span>}</td>
                <td className="px-4 py-3 text-slate-400 capitalize">{r.role ? r.role.replace("_", " ") : "—"}</td>
                <td className="px-4 py-3 text-slate-200 font-mono text-xs whitespace-nowrap">{r.ip || "—"}</td>
                <td className="px-4 py-3 text-slate-400 whitespace-nowrap" data-testid={`security-location-${r.id}`}>
                  {r.country == null ? <span className="flex items-center gap-1.5 text-slate-500"><Loader2 size={12} className="animate-spin" /> Locating…</span> : <span className="flex items-center gap-1.5"><Globe size={13} className="text-slate-500" />{r.country}</span>}
                </td>
                <td className="px-4 py-3 text-slate-400 whitespace-nowrap" title={r.user_agent || ""}><span className="flex items-center gap-1.5"><Monitor size={13} className="text-slate-500" />{deviceSummary(r.user_agent)}</span></td>
                <td className="px-4 py-3"><OutcomeBadge outcome={r.outcome} testid={`security-outcome-${r.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && <p className="text-xs text-slate-500 mt-2" data-testid="security-count">{rows.length} request{rows.length === 1 ? "" : "s"}{rows.length >= 500 ? " (showing the newest 500)" : ""}</p>}
    </div>
  );
}
